import { Link, useLocation } from 'react-router-dom';
import { Routes } from '../router/constants';
import {
  Config,
  Home,
  Logo,
  Settings,
  UsersManagement,
  Logout,
  IssueTracking,
  Notifications,
  Reports,
  StoresManagement,
  Tools,
  Archive,
  Directory,
} from '@otp/icons';
import styles from './index.module.css';
import clsx from 'clsx';
import { Delimiter, SearchInput, Typography } from '@otp/ui';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from 'src/context/auth.context';
import { Can, useCan } from 'src/utils/can';
import { Actions, AppArea, Subjects } from 'src/graphql/generated/graphql';

type LeftMenuContentProps = {
  isExpanded: boolean;
  isMobile?: boolean;
};

export const LeftMenuContent = ({
  isExpanded,
  isMobile = false,
}: LeftMenuContentProps) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'common.left-menu' });
  const { logout } = useAuthContext();
  const { can } = useCan();

  const location = useLocation();
  const currentRoutePath = location.pathname;

  const toolsRoutes = [Routes.wttpBase.path, Routes.mostBase.path];
  const canSeeTools =
    can({
      action: Actions.Read,
      subject: Subjects.Most,
      appArea: AppArea.Most,
    }) ||
    can({
      action: Actions.Read,
      subject: Subjects.Wttp,
      appArea: AppArea.Wttp,
    });

  return (
    <div className={styles.wrapper}>
      <div className={styles.logoWrapper}>
        <Link to={Routes.home.path}>
          <Logo />
        </Link>
      </div>
      <div className={styles.menuWrapper}>
        {isMobile && <SearchInput className="mb-4" fullWidth />}
        <Can I={Actions.Read} a={Subjects.Home} appArea={AppArea.Common}>
          <Link to={Routes.myOrganisation.path}>
            <div
              className={clsx(styles.menuItem, {
                [styles.menuItemActive]:
                  currentRoutePath === Routes.myOrganisation.path,
              })}
            >
              <Home />
              {isExpanded && (
                <Typography variant="body2">{t('my-organization')}</Typography>
              )}
            </div>
          </Link>
        </Can>
        <Can I={Actions.Read} a={Subjects.Users} appArea={AppArea.Common}>
          <Link to={Routes.users.path}>
            <div
              className={clsx(styles.menuItem, {
                [styles.menuItemActive]: currentRoutePath.includes(
                  Routes.users.path,
                ),
              })}
            >
              <UsersManagement />
              {isExpanded && (
                <>
                  <Can
                    I={Actions.Write}
                    a={Subjects.Users}
                    appArea={AppArea.Common}
                  >
                    <Typography variant="body2">{t('manage-otps')}</Typography>
                  </Can>
                  <Can
                    not
                    I={Actions.Write}
                    a={Subjects.Users}
                    appArea={AppArea.Common}
                  >
                    <Typography variant="body2">{t('view-otps')}</Typography>
                  </Can>
                </>
              )}
            </div>
          </Link>
        </Can>
        <Can I={Actions.Read} a={Subjects.Stores} appArea={AppArea.Common}>
          <Link to={Routes.restaurants.path}>
            <div
              className={clsx(styles.menuItem, {
                [styles.menuItemActive]: currentRoutePath.includes(
                  Routes.restaurants.path,
                ),
              })}
            >
              <StoresManagement />
              {isExpanded && (
                <Typography variant="body2">
                  {t('manage-restaurants')}
                </Typography>
              )}
            </div>
          </Link>
        </Can>
        <Can I={Actions.Read} a={Subjects.Curriculums} appArea={AppArea.Lms}>
          <Link to={Routes.lmsCurriculums.path}>
            <div
              className={clsx(styles.menuItem, {
                [styles.menuItemActive]: currentRoutePath.includes(
                  Routes.lmsCurriculums.path,
                ),
              })}
            >
              <Archive className="icon-dark" />
              {isExpanded && (
                <Typography variant="body2">{t('learning-path')}</Typography>
              )}
            </div>
          </Link>
        </Can>
        {canSeeTools && (
          <>
            <Link to={Routes.wttpConfiguration.path}>
              <div
                className={clsx(styles.menuItem, {
                  [styles.menuItemActive]: toolsRoutes.some((route) =>
                    currentRoutePath.includes(route),
                  ),
                })}
              >
                <Config />
                {isExpanded && (
                  <Typography variant="body2">{t('tools')}</Typography>
                )}
              </div>
            </Link>
            {isExpanded && (
              <div className={styles.submenuWrapper}>
                <Can I={Actions.Read} a={Subjects.Wttp} appArea={AppArea.Wttp}>
                  <Link
                    className={styles.submenuItem}
                    to={Routes.wttpConfiguration.path}
                  >
                    <Typography variant="body2">{t('wttp')}</Typography>
                  </Link>
                </Can>
                <Can I={Actions.Read} a={Subjects.Most} appArea={AppArea.Most}>
                  <Link
                    className={styles.submenuItem}
                    to={Routes.mostBase.path}
                  >
                    <Typography variant="body2">{t('most')}</Typography>
                  </Link>
                </Can>
              </div>
            )}
          </>
        )}
        <Can I={Actions.Read} a={Subjects.Issues} appArea={AppArea.Common}>
          <Link to={Routes.issueTickets.path}>
            <div
              className={clsx(styles.menuItem, {
                [styles.menuItemActive]: currentRoutePath.includes(
                  Routes.issueTickets.path,
                ),
              })}
            >
              <IssueTracking />
              {isExpanded && (
                <Typography variant="body2">{t('issue-tickets')}</Typography>
              )}
            </div>
          </Link>
        </Can>
        <Can I={Actions.Read} a={Subjects.Resources} appArea={AppArea.Common}>
          <Link to={Routes.resources.path}>
            <div
              className={clsx(styles.menuItem, {
                [styles.menuItemActive]: currentRoutePath.startsWith(
                  Routes.resources.path,
                ),
              })}
            >
              <Directory />
              {isExpanded && (
                <Typography variant="body2">{t('resources')}</Typography>
              )}
            </div>
          </Link>
        </Can>
        <Can
          I={Actions.Read}
          a={Subjects.Notifications}
          appArea={AppArea.Common}
        >
          <Link to={Routes.notificationList.path}>
            <div className={styles.menuItem}>
              <Notifications />
              {isExpanded && (
                <Typography variant="body2">{t('notifications')}</Typography>
              )}
            </div>
          </Link>
        </Can>
        <Can I={Actions.Read} a={Subjects.Reports} appArea={AppArea.Reporting}>
          <Link to={Routes.reports.path}>
            <div
              className={clsx(styles.menuItem, {
                [styles.menuItemActive]: currentRoutePath.includes(
                  Routes.reports.path,
                ),
              })}
            >
              <Reports />
              {isExpanded && (
                <Typography variant="body2">{t('reports')}</Typography>
              )}
            </div>
          </Link>
        </Can>
        <Can I={Actions.Read} a={Subjects.Admin} appArea={AppArea.Admin}>
          <Link to={Routes.admin.path}>
            <div
              className={clsx(styles.menuItem, {
                [styles.menuItemActive]: currentRoutePath.includes(
                  Routes.admin.path,
                ),
              })}
            >
              <Settings />
              {isExpanded && (
                <Typography variant="body2">{t('administration')}</Typography>
              )}
            </div>
          </Link>
          {isExpanded && (
            <div className={styles.submenuWrapper}>
              <Can
                I={Actions.Read}
                a={Subjects.Permissions}
                appArea={AppArea.Admin}
              >
                <Link
                  className={styles.submenuItem}
                  to={Routes.permissions.path}
                >
                  <Typography variant="body2">{t('permissions')}</Typography>
                </Link>
              </Can>
              <Can I={Actions.Read} a={Subjects.Roles} appArea={AppArea.Admin}>
                <Link className={styles.submenuItem} to={Routes.roles.path}>
                  <Typography variant="body2">
                    {t('permission-groups')}
                  </Typography>
                </Link>
              </Can>
              <Can
                I={Actions.Read}
                a={Subjects.UserGroups}
                appArea={AppArea.Admin}
              >
                <Link
                  className={styles.submenuItem}
                  to={Routes.userGroups.path}
                >
                  <Typography variant="body2">{t('user-groups')}</Typography>
                </Link>
              </Can>
              <Can
                I={Actions.Read}
                a={Subjects.LoginRules}
                appArea={AppArea.Admin}
              >
                <Link
                  className={styles.submenuItem}
                  to={Routes.loginRules.path}
                >
                  <Typography variant="body2">{t('login-rules')}</Typography>
                </Link>
              </Can>
              <Can
                I={Actions.Read}
                a={Subjects.Curriculums}
                appArea={AppArea.Lms}
              >
                <Link
                  className={styles.submenuItem}
                  to={Routes.lmsCurriculumsAdmin.path}
                >
                  <Typography variant="body2">{t('curriculums')}</Typography>
                </Link>
              </Can>
              <Can
                I={Actions.Read}
                a={Subjects.Certificates}
                appArea={AppArea.Lms}
              >
                <Link
                  className={styles.submenuItem}
                  to={Routes.lmsCertificates.path}
                >
                  <Typography variant="body2">{t('certificates')}</Typography>
                </Link>
              </Can>
              <Can
                I={Actions.Read}
                a={Subjects.UserMergeRequests}
                appArea={AppArea.Admin}
              >
                <Link
                  className={styles.submenuItem}
                  to={Routes.userMergeRequests.path}
                >
                  <Typography variant="body2">
                    {t('user-merge-requests')}
                  </Typography>
                </Link>
              </Can>
              <Can
                I={Actions.Read}
                a={Subjects.Resources}
                appArea={AppArea.Admin}
              >
                <Link
                  className={styles.submenuItem}
                  to={Routes.adminResources.path}
                >
                  <Typography variant="body2">{t('resources')}</Typography>
                </Link>
              </Can>
            </div>
          )}
        </Can>
        <Can I={Actions.Read} a={Subjects.Tools} appArea={AppArea.Common}>
          <Link to={Routes.tools.path}>
            <div
              className={clsx(styles.menuItem, {
                [styles.menuItemActive]: currentRoutePath.includes(
                  Routes.tools.path,
                ),
              })}
            >
              <Tools />
              {isExpanded && (
                <Typography variant="body2">{t('additional-tools')}</Typography>
              )}
            </div>
          </Link>
        </Can>

        {isMobile && (
          <>
            <Delimiter className="mb-1" />
            <Link to="" onClick={logout}>
              <div className={styles.menuItem}>
                <Logout />
                {isExpanded && (
                  <Typography variant="body2">{t('logout')}</Typography>
                )}
              </div>
            </Link>
          </>
        )}
      </div>
    </div>
  );
};
